// @ts-check
import { Plural, Trans } from '@lingui/macro';
import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Flex, SkeletonText, Text } from '~/design-system';
import Footer, {
  BackButton,
  CancelButton,
  NextButton,
} from '../../../../../components/Layout/Footer';
import Layout from '../../../../../components/Layout/Layout';
import Main from '../../../../../components/Layout/Main';
import {
  PageHeader,
  PageSubtitle,
  PageTitle,
} from '../../../../../components/Layout/PageHeader';
import SuppliersOverview, {
  useMatchSuppliers,
} from '../../../../../components/MapSuppliers/SuppliersOverview';
import { ProgressIndicator, useLink } from '../_config';

export default function All() {
  const history = useHistory();
  const link = useLink('suppliers');
  const { path } = useRouteMatch();
  /** @type {{ params : { importId: string, tab: string } } | null} */
  // We pull out the selected tab manually from the route
  const manualMatch = useRouteMatch({
    path: path.replace(/\/\w+\/?$/, '/:tab'),
  });
  const props = useMatchSuppliers({
    workspaceId: manualMatch?.params.workspaceId,
    importId: manualMatch?.params.importId,
    tab: manualMatch?.params.tab,
  });

  async function onSelectSupplier(supplierName) {
    history.push(link._all.supplier({ supplierName }));
  }

  return (
    <Layout>
      <ProgressIndicator step="matchSuppliers" />
      <PageHeader>
        <PageTitle>
          <Trans>Match suppliers</Trans>
        </PageTitle>
        <PageSubtitle>
          {props.loading ? (
            <Flex sx={{ justifyContent: 'space-around' }}>
              <SkeletonText width={100} />
            </Flex>
          ) : props.unmatchedSuppliers?.length === 0 ? (
            <Text variant="default">
              <Trans>All suppliers matched successfully</Trans>
            </Text>
          ) : (
            <React.Fragment>
              <Text variant="default">
                <Plural
                  value={props.unmatchedSuppliers?.length}
                  one={'1 supplier could not be found.'}
                  other={'# suppliers could not be found.'}
                />
              </Text>
              <PageSubtitle>
                <Trans>Please match suppliers with your partners.</Trans>
              </PageSubtitle>
            </React.Fragment>
          )}
        </PageSubtitle>
      </PageHeader>
      <Main>
        <SuppliersOverview
          sx={{
            margin: '0 auto',
            marginTop: 3,
          }}
          tab={manualMatch?.params.tab}
          onSelectSupplier={onSelectSupplier}
          link={link}
          {...props}
        />
      </Main>
      <Footer>
        <BackButton to={link.back()} />
        <CancelButton to={link.cancel()} />
        <NextButton
          onClick={async () => {
            const result = await props.saveSuppliersMatch();
            if (!result?.error) history.push(link.next());
          }}
          disabled={!props.isValid}
        />
      </Footer>
    </Layout>
  );
}
